// Cho'Gath Rupture VFX — warning circle, then ground spikes erupt from earth.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const WINDUP_MS = 1000;
const BURST_MS = 700;
const TOTAL = WINDUP_MS + BURST_MS;

// Spike positions scattered within the AoE area (relative to radius)
const SPIKE_OFFSETS = [
  { rx: 0.0,  ry: 0.0,  delay: 0.0  },
  { rx: 0.52, ry: -0.32, delay: 0.06 },
  { rx: -0.58, ry: -0.22, delay: 0.12 },
  { rx: 0.12, ry: 0.62,  delay: 0.08 },
  { rx: -0.42, ry: 0.52, delay: 0.16 },
  { rx: 0.72, ry: 0.32,  delay: 0.04 },
  { rx: -0.18, ry: -0.68, delay: 0.14 },
];

// Ground crack lines — pairs of [start_angle, bend_angle, length_factor]
const CRACK_SEGS = [
  [0.3,  0.5,  0.9],
  [1.1,  0.95, 0.75],
  [1.8,  2.0,  0.85],
  [2.6,  2.4,  0.8],
  [3.4,  3.6,  0.9],
  [4.2,  4.0,  0.7],
  [5.0,  5.2,  0.85],
  [5.8,  5.6,  0.75],
] as const;

export class ChoGathRuptureVFX implements Animation {
  private elapsed = 0;

  constructor(
    private center: Point,
    private radius: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < WINDUP_MS) {
      this.drawWarning(ctx, this.elapsed / WINDUP_MS);
    } else {
      this.drawBurst(ctx, (this.elapsed - WINDUP_MS) / BURST_MS);
    }
  }

  private drawWarning(ctx: CanvasRenderingContext2D, t: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 5);
    const urgency = Easing.easeInQuad(t);

    ctx.save();

    // Danger zone fill
    ctx.globalAlpha = (0.07 + 0.1 * urgency) * (0.7 + 0.3 * pulse);
    ctx.fillStyle = "#5C1A00";
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius, 0, Math.PI * 2);
    ctx.fill();

    // Outer warning ring
    ctx.globalAlpha = (0.5 + 0.4 * urgency) * (0.6 + 0.4 * pulse);
    ctx.strokeStyle = "#CC3300";
    ctx.lineWidth = 3 + urgency * 2;
    ctx.shadowColor = "#8B2200";
    ctx.shadowBlur = 10 + urgency * 16;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner ring
    ctx.globalAlpha = (0.25 + 0.25 * urgency) * (0.5 + 0.5 * pulse);
    ctx.strokeStyle = "#E8855A";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * 0.45, 0, Math.PI * 2);
    ctx.stroke();

    // Ground cracks spreading inward from edge
    if (t > 0.5) {
      const crackT = (t - 0.5) / 0.5;
      this.drawGroundCracks(ctx, crackT * 0.6, crackT * 0.55 * (0.6 + 0.4 * pulse));
    }

    // Dirt particles starting to rise near end of windup
    if (t > 0.75) {
      const dustT = (t - 0.75) / 0.25;
      ctx.globalAlpha = dustT * 0.4 * pulse;
      ctx.fillStyle = "#8B4513";
      ctx.shadowColor = "#5C2A00";
      ctx.shadowBlur = 4;
      for (const sp of SPIKE_OFFSETS) {
        const px = this.center.x + sp.rx * this.radius;
        const py = this.center.y + sp.ry * this.radius;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawBurst(ctx: CanvasRenderingContext2D, t: number): void {
    const globalFade = t < 0.2 ? 1 : 1 - (t - 0.2) / 0.8;

    ctx.save();

    // Ground flash at start of burst
    if (t < 0.25) {
      ctx.globalAlpha = (1 - t / 0.25) * 0.45;
      ctx.fillStyle = "#D2691E";
      ctx.shadowColor = "#8B4513";
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.arc(this.center.x, this.center.y, this.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Ground cracks — remain visible and expand fully, then fade
    this.drawGroundCracks(ctx, Math.min(t * 3, 1), globalFade * 0.75);

    // Each spike erupts from the ground
    for (const sp of SPIKE_OFFSETS) {
      const spikeT = Math.max(0, (t - sp.delay) / (1 - sp.delay));
      if (spikeT <= 0) continue;
      const px = this.center.x + sp.rx * this.radius;
      const py = this.center.y + sp.ry * this.radius;
      this.drawSpike(ctx, px, py, spikeT, globalFade);
    }

    // Debris rocks flying outward from center
    if (t < 0.5) {
      const debrisT = t / 0.5;
      ctx.shadowBlur = 0;
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 + 0.4;
        const dist = this.radius * 0.3 + this.radius * 0.85 * Easing.easeOutQuad(debrisT);
        const px = this.center.x + Math.cos(angle) * dist;
        const py = this.center.y + Math.sin(angle) * dist;
        const size = 2 + (i % 3);
        ctx.globalAlpha = globalFade * (1 - debrisT) * 0.8;
        ctx.fillStyle = i % 2 === 0 ? "#8B4513" : "#5C3317";
        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawGroundCracks(ctx: CanvasRenderingContext2D, progress: number, alpha: number): void {
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#5C2A00";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#CC4400";
    ctx.shadowBlur = 5;

    for (const [startAngle, midAngle, lenFactor] of CRACK_SEGS) {
      const fullLen = this.radius * lenFactor;
      const mid = fullLen * 0.45;
      const outerLen = fullLen * progress;

      // First segment from edge inward
      const ex = this.center.x + Math.cos(startAngle) * this.radius;
      const ey = this.center.y + Math.sin(startAngle) * this.radius;
      const mx = this.center.x + Math.cos(midAngle) * (this.radius - mid);
      const my = this.center.y + Math.sin(midAngle) * (this.radius - mid);

      // Draw crack from edge toward center by 'progress'
      const dx = mx - ex;
      const dy = my - ey;
      ctx.beginPath();
      ctx.moveTo(ex, ey);
      ctx.lineTo(ex + dx * Math.min(progress * 2, 1), ey + dy * Math.min(progress * 2, 1));
      ctx.stroke();

      // Branch segment
      if (progress > 0.4) {
        const branchT = (progress - 0.4) / 0.6;
        const branchAngle = midAngle + 0.5;
        const bx = mx + Math.cos(branchAngle) * this.radius * 0.25 * branchT;
        const by = my + Math.sin(branchAngle) * this.radius * 0.25 * branchT;
        ctx.globalAlpha = alpha * 0.6;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(mx, my);
        ctx.lineTo(bx, by);
        ctx.stroke();
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = alpha;
      }
    }
  }

  private drawSpike(ctx: CanvasRenderingContext2D, x: number, y: number, t: number, fade: number): void {
    // Spike rises fast then holds briefly then fades
    const riseT = Math.min(t * 4, 1);
    const scale = Easing.easeOutBack(riseT);
    const spikeFade = riseT < 1 ? 1 : Math.max(0, 1 - (t - 0.25) / 0.75);

    const h = this.radius * 0.72 * scale;  // height (pointing up on screen)
    const w = this.radius * 0.14 * scale;  // half-width at base

    if (h < 1) return;

    ctx.save();

    // Shadow ellipse at base (gives ground depth)
    ctx.globalAlpha = fade * spikeFade * 0.5;
    ctx.fillStyle = "#1a0800";
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.ellipse(x, y + h * 0.08, w * 1.2, w * 0.45, 0, 0, Math.PI * 2);
    ctx.fill();

    // Spike body — kite/diamond pointing upward
    // tip = top, base wings = sides, bottom = slightly below center
    const grad = ctx.createLinearGradient(x - w, y, x + w, y);
    grad.addColorStop(0, "#3B1A0A");
    grad.addColorStop(0.35, "#8B4513");
    grad.addColorStop(0.55, "#C4622D");
    grad.addColorStop(1, "#2A0F05");
    ctx.globalAlpha = fade * spikeFade;
    ctx.fillStyle = grad;
    ctx.shadowColor = "#CC4400";
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(x, y - h);          // tip (top)
    ctx.lineTo(x + w, y);          // right wing
    ctx.lineTo(x, y + h * 0.15);   // base bottom
    ctx.lineTo(x - w, y);          // left wing
    ctx.closePath();
    ctx.fill();

    // Bright highlight on left face (light from upper-left)
    ctx.globalAlpha = fade * spikeFade * 0.75;
    ctx.strokeStyle = "#E8855A";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#FF6633";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(x, y - h);
    ctx.lineTo(x - w, y);
    ctx.stroke();

    // Hot glowing tip
    ctx.globalAlpha = fade * spikeFade * 0.6;
    ctx.fillStyle = "#FFAA55";
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.arc(x, y - h, w * 0.35, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
